import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getAreSearchFacetsCurrent, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { parseQueryParams } from '@/utils/queryParams';
import { canonicalizeCategoryIdSearchParams } from '@/utils/search/canonicalizeCategoryIdSearchParams';
import { urlOnlyRewriteRouterState } from '@/utils/urlOnlyRewriteRouterState';
import { buildQueryUrl, getSearchBaseUrl } from '@/utils/urls';

/**
 * Followed-search links, alert emails and old bookmarks carry creator / style / material filters as ids on
 * `category_id`. Once the facets for this search are in, swap those ids for the semantic params a live search
 * uses, so the URL reads like the original search and its filter chips can be removed.
 */
export const useCanonicalizeCategoryIdSearchUrl = () => {
    const navigate = useNavigate();
    const { hash, key, pathname, search } = useLocation();
    const searchFacets = useAppSelector(getSearchFacets);
    const areSearchFacetsCurrent = useAppSelector(getAreSearchFacetsCurrent);
    const { isInitialLoading, submitted } = useAppSelector(getSearchUiState);
    const canonicalizedLocationKeyRef = useRef<string | null>(null);
    const loadingSeenForLocationKeyRef = useRef<string | null>(null);

    useEffect(() => {
        // Browse (`/c/`) and collection pages share SearchPage but encode their dimensions elsewhere.
        const isKeywordSearchPath = `${pathname}/`.startsWith(getSearchBaseUrl());
        if (!isKeywordSearchPath || canonicalizedLocationKeyRef.current === key) {
            return;
        }
        if (isInitialLoading || submitted) {
            loadingSeenForLocationKeyRef.current = key;
            return;
        }
        // On a client-side arrival this effect runs before RouteDataLoader's (a parent) starts the search, so a
        // "not loading" reading still shows the previous page's facets. Trust it only once this location has loaded,
        // or on the server-rendered entry (`default`), whose facets came with the page.
        if (!areSearchFacetsCurrent || (key !== 'default' && loadingSeenForLocationKeyRef.current !== key)) {
            return;
        }

        const queryParams = parseQueryParams(search);
        const canonicalParams = canonicalizeCategoryIdSearchParams(queryParams, searchFacets);
        if (canonicalParams === queryParams) {
            return;
        }

        canonicalizedLocationKeyRef.current = key;
        // The page already shows what the canonical URL describes, so don't search again or jump to the top.
        navigate(`${buildQueryUrl(pathname, canonicalParams)}${hash}`, {
            replace: true,
            state: urlOnlyRewriteRouterState,
        });
    }, [areSearchFacetsCurrent, hash, isInitialLoading, key, navigate, pathname, search, searchFacets, submitted]);
};
